import _ from 'lodash';
import {
  TextCollectionField,
  FieldSet,
  FormField,
  TextCollectionFieldType,
  TextField,
  FileUploadAnnotation,
  CodeAnnotation,
} from '@scale/shared/types/params/textcollection';
import {
  TextCollectionAnnotation,
  FieldSetAnnotation,
  isFieldSetAnnotation,
  QueryableTextAndResultsAnnotationEntry,
} from '@scale/shared/types/response/textcollection';
import { isBlankField } from '@scale/shared/textcollection/helpers';

export function splitFieldSets(fields: TextCollectionField[]): [TextCollectionField[], FieldSet[]] {
  const formFields: TextCollectionField[] = [];
  const fieldSets: FieldSet[] = [];
  for (const field of fields) {
    if (isFieldSet(field)) {
      fieldSets.push(field);
    } else {
      formFields.push(field);
    }
  }
  return [formFields, fieldSets];
}

export function getFieldSets(fields: TextCollectionField[]): FieldSet[] {
  return fields.filter(isFieldSet);
}

export function getForms(fields: TextCollectionField[]): FormField[] {
  return fields.filter(isForm);
}

interface Validation {
  isBlank: boolean;
}

export function getFieldValidations(
  fieldSet: FieldSet,
  annotations: Record<string, TextCollectionAnnotation>,
): Record<string, Validation> {
  const validations: Record<string, Validation> = {};
  for (const field of fieldSet.fields) {
    const isBlank = isBlankField(annotations[field.field_id]);
    const validation: Validation = {
      isBlank,
    };
    validations[field.field_id] = validation;
  }
  return validations;
}

function isForm(field: TextCollectionField): field is FormField {
  return field.type === TextCollectionFieldType.Form;
}

export function isFieldSet(field: TextCollectionField): field is FieldSet {
  return field.type === TextCollectionFieldType.FieldSet;
}

export function getFieldSetAnnotation(
  annotations: Record<string, TextCollectionAnnotation>,
  id: string,
): FieldSetAnnotation {
  const fieldSetAnnotation = annotations[id];
  if (!fieldSetAnnotation || fieldSetAnnotation.type !== TextCollectionFieldType.FieldSet) {
    return {
      type: TextCollectionFieldType.FieldSet,
      uuids: [],
      field_id: id,
      response: [],
    };
  }
  return fieldSetAnnotation;
}

export interface FieldSetCount {
  id: string;
  title: string;
  count: number;
}

export function getFieldSetCounts(
  fields: TextCollectionField[],
  annotations: Record<string, TextCollectionAnnotation>,
): FieldSetCount[] {
  const fieldSetCounts = getFieldSets(fields).map(fieldSet => {
    const fieldSetAnnotation = getFieldSetAnnotation(annotations, fieldSet.field_id);
    return {
      id: fieldSet.field_id,
      title: fieldSet.title || fieldSet.field_id,
      count: fieldSetAnnotation.response.length,
    };
  });
  return fieldSetCounts;
}

export function getFirstTextField(fieldSet: FieldSet): TextField | undefined {
  for (const field of fieldSet.fields) {
    if (field.type === TextCollectionFieldType.Text) {
      return field;
    }
  }
}

// FieldSets use the first text field as a friendly identifier in the user interface.
export function getFieldSetTitle(
  fieldSet: FieldSet,
  annotations: Record<string, TextCollectionAnnotation>,
): string {
  const firstField = getFirstTextField(fieldSet) || fieldSet.fields[0];
  const annotation = annotations?.[firstField.field_id];
  if (annotation && annotation.response[0]) {
    const responseData = annotation.response[0];
    if (annotation.type === TextCollectionFieldType.QueryableTextAndResults) {
      return (responseData as QueryableTextAndResultsAnnotationEntry)?.searchTerm;
    } else if (annotation.type === TextCollectionFieldType.FileUpload) {
      return (responseData as FileUploadAnnotation)?.name ?? '';
    } else if (annotation.type === TextCollectionFieldType.Code) {
      return (responseData as CodeAnnotation)?.sourceCode.slice(0, 100) ?? '';
    }
    return _.isString(responseData) ? responseData : JSON.stringify(responseData);
  }
  if (fieldSet.title) {
    return fieldSet.title;
  }
  return fieldSet.field_id;
}

// Finds children with missing details; returns a record of field title
// to number of children at that level missing required fields.
export const getChildFieldMissingDetailCounts = (
  response: Record<string, TextCollectionAnnotation>,
  field: FieldSet,
): Record<string, number> => {
  let result: Record<string, number> = {};
  let currentAddedDepth: number | undefined;

  const addChildren = (
    annotations: Record<string, TextCollectionAnnotation>,
    fieldSet: FieldSet,
    depth: number = 0,
    parent?: string,
  ) => {
    if (!_.isNil(currentAddedDepth) && depth > currentAddedDepth) {
      return;
    }
    let addedUnitMissing = false;
    const [childFieldSets, childUnitFields] = _.partition(
      fieldSet.fields,
      subField => subField.type === TextCollectionFieldType.FieldSet,
    );

    for (const childUnitField of childUnitFields) {
      const child = _.find(
        _.values(annotations),
        childEntry => childEntry.field_id === childUnitField.field_id,
      );
      if (parent && !addedUnitMissing) {
        if (isBlankField(child) && childUnitField.required) {
          // If added an error at a deeper node, remove
          if (!_.isNil(currentAddedDepth) && depth < currentAddedDepth) {
            result = {};
          }
          currentAddedDepth = depth;
          if (!result[parent]) {
            result[parent] = 0;
          }
          result[parent] += 1;
          addedUnitMissing = true;
        }
      }
    }
    // Only show the highest-level field with details missing
    if (addedUnitMissing) {
      return;
    }
    for (const childField of childFieldSets) {
      if (!childField) {
        continue;
      }
      const child = _.find(
        _.values(annotations),
        childEntry => childEntry.field_id === childField.field_id,
      );
      if (child && isFieldSetAnnotation(child)) {
        child.response.forEach(response => {
          addChildren(response, childField as FieldSet, depth + 1, childField.title);
        });
      }
    }
  };

  addChildren(response, field);

  return result;
};
